<template>
  <div class="p-4 sm:p-6">
    <!-- Header -->
    <div
      class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
    >
      <div>
        <h1 class="text-xl sm:text-2xl font-bold text-gray-800">
          Programme de fidélité
        </h1>
        <p class="text-sm text-gray-500 mt-1">
          Gérez les niveaux et les jalons de récompenses
        </p>
      </div>
      <button
        @click="openCreator"
        class="w-full sm:w-auto px-4 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-center"
      >
        <i class="pi pi-plus mr-2"></i>
        {{ activeTab === 'levels' ? 'Ajouter un niveau' : activeTab === 'milestones' ? 'Ajouter un jalon' : 'Ajouter une règle' }}
      </button>
    </div>

    <!-- Tabs -->
    <div
      class="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1 w-full sm:w-fit overflow-x-auto"
    >
      <button
        v-for="tab in tabs"
        :key="tab.key"
        @click="activeTab = tab.key"
        class="flex-1 sm:flex-none px-4 sm:px-5 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
        :class="
          activeTab === tab.key
            ? 'bg-white text-gray-900 shadow-sm'
            : 'text-gray-500 hover:text-gray-700'
        "
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- === NIVEAUX === -->
    <div v-if="activeTab === 'levels'">
      <DataFetcher
        dataKey="rewardLevels"
        ref="levelFetcher"
        @fetched="onLevelsLoaded"
      >
        <template #default="{ loading, data }">
          <div v-if="loading" class="space-y-4">
            <div
              v-for="i in 3"
              :key="i"
              class="shimmerElement roundedShimmer p-5 border border-gray-200"
            >
              <div class="h-5 bg-gray-100 rounded w-40 mb-2"></div>
              <div class="h-4 bg-gray-100 rounded w-60"></div>
            </div>
          </div>

          <div v-else class="space-y-4">
            <div
              v-for="level in data?.result || []"
              :key="level.id"
              class="border border-gray-200 bg-white rounded-xl p-4 sm:p-5 hover:border-gray-300 transition-colors"
            >
              <div
                class="flex flex-col lg:flex-row lg:items-center justify-between gap-4"
              >
                <!-- Info Principale -->
                <div
                  class="flex items-start sm:items-center gap-3 sm:gap-4 min-w-0"
                >
                  <div
                    class="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-50 rounded-xl flex items-center justify-center shrink-0"
                  >
                    <span
                      class="text-lg sm:text-xl font-bold text-indigo-600"
                      >{{ level.level }}</span
                    >
                  </div>
                  <div class="min-w-0">
                    <h3 class="font-semibold text-gray-800 truncate">
                      {{ level.name }}
                    </h3>
                    <p class="text-sm text-gray-500 break-words">
                      {{ level.description }}
                    </p>
                  </div>
                </div>

                <!-- Métriques & Actions -->
                <div
                  class="flex flex-wrap items-center justify-between lg:justify-end gap-3 sm:gap-6 text-sm pt-3 lg:pt-0 border-t lg:border-t-0 border-gray-100"
                >
                  <div class="flex items-center gap-3">
                    <div>
                      <span class="text-gray-400">XP :</span>
                      <span class="ml-1 font-semibold text-gray-800">{{
                        level.requiredXp
                      }}</span>
                    </div>
                    <span
                      class="text-xs px-2.5 py-1 rounded-full font-medium"
                      :class="
                        level.isActive
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-500'
                      "
                    >
                      {{ level.isActive ? 'Actif' : 'Inactif' }}
                    </span>
                  </div>

                  <div class="flex items-center gap-2">
                    <button
                      @click="editLevel(level)"
                      class="px-3 py-1.5 text-xs sm:text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-gray-200"
                    >
                      Modifier
                    </button>
                    <button
                      @click="deleteLevel(level)"
                      class="px-3 py-1.5 text-xs sm:text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-gray-200"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="!data?.result?.length"
              class="text-center py-12 sm:py-16"
            >
              <div
                class="w-14 h-14 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <i class="pi pi-star text-2xl text-gray-400"></i>
              </div>
              <h3 class="text-lg font-semibold text-gray-700 mb-2">
                Aucun niveau configuré
              </h3>
              <p class="text-sm text-gray-500 mb-4">
                Commencez par créer le premier niveau du programme
              </p>
              <button
                @click="openCreator"
                class="px-4 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800"
              >
                Créer un niveau
              </button>
            </div>
          </div>
        </template>
      </DataFetcher>
    </div>

    <!-- === JALONS === -->
    <div v-if="activeTab === 'milestones'">
      <DataFetcher
        dataKey="milestones"
        ref="milestoneFetcher"
        @fetched="onMilestonesLoaded"
      >
        <template #default="{ loading, data }">
          <div v-if="loading" class="space-y-4">
            <div
              v-for="i in 3"
              :key="i"
              class="shimmerElement roundedShimmer p-5 border border-gray-200"
            >
              <div class="h-5 bg-gray-100 rounded w-40 mb-2"></div>
              <div class="h-4 bg-gray-100 rounded w-60"></div>
            </div>
          </div>

          <div v-else class="space-y-4">
            <div
              v-for="milestone in data?.result || []"
              :key="milestone.id"
              class="border border-gray-200 bg-white rounded-xl p-4 sm:p-5 hover:border-gray-300 transition-colors"
            >
              <div
                class="flex flex-col lg:flex-row lg:items-center justify-between gap-4"
              >
                <!-- Info Principale -->
                <div
                  class="flex items-start sm:items-center gap-3 sm:gap-4 min-w-0"
                >
                  <div
                    class="w-10 h-10 sm:w-12 sm:h-12 bg-amber-50 rounded-xl flex items-center justify-center shrink-0"
                  >
                    <i class="pi pi-flag text-lg sm:text-xl text-amber-500"></i>
                  </div>
                  <div class="min-w-0">
                    <h3 class="font-semibold text-gray-800 truncate">
                      {{ milestone.name }}
                    </h3>
                    <p class="text-sm text-gray-500 break-words">
                      Type : {{ milestone.type }} — Objectif :
                      {{ milestone.targetValue }}
                    </p>
                  </div>
                </div>

                <!-- Métriques & Actions -->
                <div
                  class="flex flex-wrap items-center justify-between lg:justify-end gap-3 sm:gap-6 text-sm pt-3 lg:pt-0 border-t lg:border-t-0 border-gray-100"
                >
                  <div class="flex flex-wrap items-center gap-3">
                    <div>
                      <span class="text-gray-400">XP :</span>
                      <span class="ml-1 font-semibold text-indigo-600"
                        >+{{ milestone.rewardXp }}</span
                      >
                    </div>
                    <div>
                      <span class="text-gray-400">Points :</span>
                      <span class="ml-1 font-semibold text-amber-600"
                        >+{{ milestone.rewardPoints }}</span
                      >
                    </div>
                    <span
                      class="text-xs px-2.5 py-1 rounded-full font-medium"
                      :class="
                        milestone.isActive
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-500'
                      "
                    >
                      {{ milestone.isActive ? 'Actif' : 'Inactif' }}
                    </span>
                  </div>

                  <div class="flex items-center gap-2">
                    <button
                      @click="editMilestone(milestone)"
                      class="px-3 py-1.5 text-xs sm:text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-gray-200"
                    >
                      Modifier
                    </button>
                    <button
                      @click="deleteMilestone(milestone)"
                      class="px-3 py-1.5 text-xs sm:text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-gray-200"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="!data?.result?.length"
              class="text-center py-12 sm:py-16"
            >
              <div
                class="w-14 h-14 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <i class="pi pi-trophy text-2xl text-gray-400"></i>
              </div>
              <h3 class="text-lg font-semibold text-gray-700 mb-2">
                Aucun jalon configuré
              </h3>
              <p class="text-sm text-gray-500 mb-4">
                Créez des défis pour récompenser vos utilisateurs
              </p>
              <button
                @click="openCreator"
                class="px-4 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800"
              >
                Créer un jalon
              </button>
            </div>
          </div>
        </template>
      </DataFetcher>
    </div>

    <!-- === RÈGLES === -->
    <div v-if="activeTab === 'rules'">
      <DataFetcher
        dataKey="rewardRules"
        ref="ruleFetcher"
        @fetched="onRulesLoaded"
      >
        <template #default="{ loading, data }">
          <div v-if="loading" class="space-y-4">
            <div
              v-for="i in 3"
              :key="i"
              class="shimmerElement roundedShimmer p-5 border border-gray-200"
            >
              <div class="h-5 bg-gray-100 rounded w-40 mb-2"></div>
              <div class="h-4 bg-gray-100 rounded w-60"></div>
            </div>
          </div>

          <div v-else class="space-y-4">
            <div
              v-for="rule in data?.result || []"
              :key="rule.id || rule.event"
              class="border border-gray-200 bg-white rounded-xl p-4 sm:p-5 hover:border-gray-300 transition-colors"
            >
              <div
                class="flex flex-col lg:flex-row lg:items-center justify-between gap-4"
              >
                <!-- Info Principale -->
                <div
                  class="flex items-start sm:items-center gap-3 sm:gap-4 min-w-0"
                >
                  <div
                    class="w-10 h-10 sm:w-12 sm:h-12 bg-blue-50 rounded-xl flex items-center justify-center shrink-0"
                  >
                    <i class="pi pi-bolt text-lg sm:text-xl text-blue-500"></i>
                  </div>
                  <div class="min-w-0">
                    <h3 class="font-semibold text-gray-800 truncate">
                      {{ rule.name || rule.event }}
                    </h3>
                    <p class="text-sm text-gray-500 break-words">
                      Événement : {{ rule.event }}
                    </p>
                  </div>
                </div>

                <!-- Métriques & Actions -->
                <div
                  class="flex flex-wrap items-center justify-between lg:justify-end gap-3 sm:gap-6 text-sm pt-3 lg:pt-0 border-t lg:border-t-0 border-gray-100"
                >
                  <div class="flex flex-wrap items-center gap-3">
                    <div>
                      <span class="text-gray-400">XP :</span>
                      <span class="ml-1 font-semibold text-indigo-600"
                        >+{{ rule.xp }}</span
                      >
                    </div>
                    <div>
                      <span class="text-gray-400">Points :</span>
                      <span class="ml-1 font-semibold text-amber-600"
                        >+{{ rule.points }}</span
                      >
                    </div>
                    <span
                      class="text-xs px-2.5 py-1 rounded-full font-medium"
                      :class="
                        rule.isActive
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-500'
                      "
                    >
                      {{ rule.isActive ? 'Actif' : 'Inactif' }}
                    </span>
                  </div>

                  <div class="flex items-center gap-2">
                    <button
                      @click="editRule(rule)"
                      class="px-3 py-1.5 text-xs sm:text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-gray-200"
                    >
                      Modifier
                    </button>
                    <button
                      @click="deleteRule(rule)"
                      class="px-3 py-1.5 text-xs sm:text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-gray-200"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="!data?.result?.length"
              class="text-center py-12 sm:py-16"
            >
              <div
                class="w-14 h-14 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <i class="pi pi-bolt text-2xl text-gray-400"></i>
              </div>
              <h3 class="text-lg font-semibold text-gray-700 mb-2">
                Aucune règle configurée
              </h3>
              <p class="text-sm text-gray-500 mb-4">
                Créez des règles d'attribution de points
              </p>
              <button
                @click="openCreator"
                class="px-4 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800"
              >
                Créer une règle
              </button>
            </div>
          </div>
        </template>
      </DataFetcher>
    </div>

    <!-- Panel de création -->
    <SlideDetailView :isVisible="!!showCreator" @hide="showCreator = false">
      <LevelDetails
        v-if="showCreator && activeTab === 'levels'"
        :levelData="selectedLevel"
        @hide="showCreator = false"
        @saved="payload => saveLevelTrigger?.trigger(payload)"
      />
      <MilestoneEditor
        v-else-if="showCreator && activeTab === 'milestones'"
        :milestoneData="selectedMilestone"
        @cancel="showCreator = false"
        @saved="payload => saveMilestoneTrigger?.trigger(payload)"
      />
      <RuleEditor
        v-else-if="showCreator && activeTab === 'rules'"
        :ruleData="selectedRule"
        @cancel="showCreator = false"
        @saved="payload => saveRuleTrigger?.trigger(payload)"
      />
    </SlideDetailView>

    <!-- Action Triggers -->
    <ActionTrigger
      dataKey="saveLevel"
      ref="saveLevelTrigger"
      @success="onLevelSaved"
      @failed="onError"
    />
    <ActionTrigger
      dataKey="deleteLevel"
      ref="deleteLevelTrigger"
      @success="onLevelDeleted"
      @failed="onError"
    />
    <ActionTrigger
      dataKey="saveMilestone"
      ref="saveMilestoneTrigger"
      @success="onMilestoneSaved"
      @failed="onError"
    />
    <ActionTrigger
      dataKey="deleteMilestone"
      ref="deleteMilestoneTrigger"
      @success="onMilestoneDeleted"
      @failed="onError"
    />
    <ActionTrigger
      dataKey="saveRewardRule"
      ref="saveRuleTrigger"
      @success="onRuleSaved"
      @failed="onError"
    />
    <ActionTrigger
      dataKey="deleteRewardRule"
      ref="deleteRuleTrigger"
      @success="onRuleDeleted"
      @failed="onError"
    />
  </div>
</template>

<script setup>
import { ref } from 'vue'
import DataFetcher from '@/features/data/presentation/components/DataFetcher.vue'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import SlideDetailView from '@/project/components/others/SlideDetailView.vue'
import LevelDetails from '@/project/components/milestones/LevelDetails.vue'
import MilestoneEditor from '@/project/components/gamification/admin/MilestoneEditor.vue'
import RuleEditor from '@/project/components/gamification/admin/RuleEditor.vue'
import { EVENTS } from '@/shared/constants/commons'
import Events from '@/events/events'
import DataService from '@/features/data/dataService.js'

const activeTab = ref('levels')
const showCreator = ref(false)
const selectedLevel = ref(null)
const selectedMilestone = ref(null)
const selectedRule = ref(null)
const levelFetcher = ref(null)
const milestoneFetcher = ref(null)
const ruleFetcher = ref(null)
const saveLevelTrigger = ref(null)
const deleteLevelTrigger = ref(null)
const saveMilestoneTrigger = ref(null)
const deleteMilestoneTrigger = ref(null)
const saveRuleTrigger = ref(null)
const deleteRuleTrigger = ref(null)

const tabs = [
  { key: 'levels', label: 'Niveaux' },
  { key: 'milestones', label: 'Jalons / Défis' },
  { key: 'rules', label: 'Règles' },
]

function openCreator() {
  selectedLevel.value = null
  selectedMilestone.value = null
  selectedRule.value = null
  showCreator.value = true
}

function editLevel(level) {
  selectedLevel.value = level
  showCreator.value = true
}

function editMilestone(milestone) {
  selectedMilestone.value = milestone
  showCreator.value = true
}

function onLevelsLoaded({ data }) {
  console.log('Levels loaded:', data)
}

function onMilestonesLoaded({ data }) {
  console.log('Milestones loaded:', data)
}

function onRulesLoaded({ data }) {
  console.log('Rules loaded:', data)
}

function editRule(rule) {
  selectedRule.value = rule
  showCreator.value = true
}

async function deleteLevel(level) {
  const res = await window.customPrompt({
    title: `Supprimer le niveau "${level.name}" ?`,
    formFields: [],
    successText: 'Supprimer',
    cancelText: 'Retour',
  })
  if (!res.accepted) return

  const result = await DataService.triggerAction('deleteLevel', {
    id: level.id,
  })
  if (result?.statusCode === 200) {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Supprimé',
      body: 'Niveau supprimé avec succès.',
      type: 'success',
    })
    levelFetcher.value?.refresh()
  }
}

async function deleteMilestone(milestone) {
  const res = await window.customPrompt({
    title: `Supprimer le jalon "${milestone.name}" ?`,
    formFields: [],
    successText: 'Supprimer',
    cancelText: 'Retour',
  })
  if (!res.accepted) return

  const result = await DataService.triggerAction('deleteMilestone', {
    id: milestone.id,
  })
  if (result?.statusCode === 200) {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Supprimé',
      body: 'Jalon supprimé avec succès.',
      type: 'success',
    })
    milestoneFetcher.value?.refresh()
  }
}

async function deleteRule(rule) {
  const res = await window.customPrompt({
    title: `Supprimer la règle "${rule.name || rule.event}" ?`,
    formFields: [],
    successText: 'Supprimer',
    cancelText: 'Retour',
  })
  if (!res.accepted) return

  const result = await DataService.triggerAction('deleteRewardRule', {
    id: rule.id || rule.event,
  })
  if (result?.statusCode === 200) {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Supprimé',
      body: 'Règle supprimée avec succès.',
      type: 'success',
    })
    ruleFetcher.value?.refresh()
  }
}

function onLevelSaved() {
  showCreator.value = false
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Succès',
    body: 'Niveau sauvegardé avec succès.',
  })
  levelFetcher.value?.refresh()
}

function onLevelDeleted() {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Supprimé',
    body: 'Niveau supprimé.',
  })
  levelFetcher.value?.refresh()
}

function onMilestoneSaved() {
  showCreator.value = false
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Succès',
    body: 'Jalon sauvegardé avec succès.',
  })
  milestoneFetcher.value?.refresh()
}

function onMilestoneDeleted() {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Supprimé',
    body: 'Jalon supprimé.',
  })
  milestoneFetcher.value?.refresh()
}

function onRuleSaved() {
  showCreator.value = false
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Succès',
    body: 'Règle sauvegardée avec succès.',
  })
  ruleFetcher.value?.refresh()
}

function onRuleDeleted() {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Supprimé',
    body: 'Règle supprimée.',
  })
  ruleFetcher.value?.refresh()
}

function onError(err) {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Erreur',
    body: err?.result?.message || 'Une erreur est survenue.',
  })
}
</script>
